import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Empty,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { PayEventModal } from './PayEventModal'
import { SalaryItemModal } from './SalaryItemModal'
import {
  fetchPayComponents,
  fetchPayEvents,
  fetchSalary,
  fetchSalaryHistory,
  netOfItems,
  stopSalaryItem,
  type PayEvent,
  type PayItem,
} from './payApi'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  personId: string
}

/**
 * Maosh tarkibi — doimiy qismlar, bir martalik to'lovlar va tarix.
 *
 * <h2>⚠️ HUQUQSIZ SO'ROV UMUMAN YUBORILMAYDI</h2>
 * `hr.salary.view` hech bir tizim roliga o'z-o'zidan tushmaydi va bu
 * ATAYLAB (`V202608150700`). Shuning uchun bu blokda uchta narsa
 * birga bo'lishi shart:
 * <ul>
 *   <li>huquq yo'q bo'lsa `useQuery` ham ISHGA TUSHMAYDI
 *       (`enabled`) — 403 javobining o'zi ham «bu odamda maosh bor»
 *       degan faktni tasdiqlardi;</li>
 *   <li>maydonlar `***` bilan ham chizilmaydi — niqoblangan maydon
 *       «bu yerda son bor» deb turadi;</li>
 *   <li>o'rniga SABAB va yetishmayotgan huquq KODI yoziladi
 *       (`PermissionNotice`) — administrator uni rol konstruktorida
 *       aynan shu nom bilan qidiradi.</li>
 * </ul>
 *
 * <h2>⚠️ «Qo'lga tegadigan» — TAXMIN, VEDOMOST EMAS</h2>
 * Bu yerdagi jam faqat OCHIQ doimiy qismlardan chiqadi. Haqiqiy hisob
 * vedomostda: u bir martalik hodisalarni ham oladi va ishdan
 * bo'shaganni chiqarib tashlaydi. Shuning uchun raqam yonida shu ochiq
 * yoziladi — aks holda ikki ekrandagi ikki xil son «qaysi biri
 * to'g'ri?» degan savolni tug'dirardi.
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q</h2>
 * Doimiy qism to'xtatiladi (davri yopiladi), o'chirilmaydi: tarix
 * o'zgarmas va u maosh hisobotining manbai. Bir martalik hodisa esa
 * vedomostga tushgach (`settled`) umuman tegilmaydi.
 */
export function SalaryCard({ personId }: Props) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canView = can('hr.salary.view')
  const canWrite = can('hr.salary.write')

  // ⚠️ N4: `pay_item.changed_by` — `auth.getName()`, aktyor
  //    identifikatori. Nom `actorName.ts` xaritasidan keladi.
  const actors = useActorDirectory()

  const [setOpen, setSetOpen] = useState(false)
  const [eventOpen, setEventOpen] = useState(false)
  const [stopTarget, setStopTarget] = useState<PayItem | null>(null)
  const [stopOn, setStopOn] = useState<Dayjs>(() => tenantToday())

  const runner = useOperationRunner([queryKeys.hr.all])

  const current = useQuery({
    queryKey: queryKeys.hr.salary(personId),
    queryFn: () => fetchSalary(personId),
    enabled: canView && personId !== '',
  })

  const history = useQuery({
    queryKey: queryKeys.hr.salaryHistory(personId),
    queryFn: () => fetchSalaryHistory(personId),
    enabled: canView && personId !== '',
  })

  const events = useQuery({
    queryKey: queryKeys.hr.payEvents(personId),
    queryFn: () => fetchPayEvents(personId),
    enabled: canView && personId !== '',
  })

  // ⚠️ Katalog FAQAT yozish huquqi bilan kerak — u tanlov ro'yxati
  // uchun. Ko'rish huquqi bor, lekin yozish huquqi yo'q operatorga
  // ortiqcha so'rov yuborilmaydi.
  const components = useQuery({
    queryKey: queryKeys.hr.payComponents(true),
    queryFn: () => fetchPayComponents(true),
    enabled: canView && canWrite,
    staleTime: 5 * 60_000,
  })

  if (!canView) {
    return (
      <Card title={t('hr.salary.title')}>
        <PermissionNotice
          permission="hr.salary.view"
          title={t('hr.salary.hiddenTitle')}
          reason={t('hr.salary.hiddenReason')}
        />
      </Card>
    )
  }

  const items = current.data ?? []
  const net = netOfItems([...items])
  const recurringComponents = (components.data ?? []).filter((c) => c.recurring)
  const oneOffComponents = (components.data ?? []).filter((c) => !c.recurring)

  const itemColumns: ColumnsType<PayItem> = [
    {
      // Uchala jadval ham sahifalanmaydi — raqam `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.componentName ? seededName('payComponents', row.componentCode, row.componentName, t) : (row.componentCode ?? '—')}</span>
          <Tag color={row.kind === 'EARNING' ? 'green' : 'volcano'}>
            {t(`payComponentKind.${row.kind}`)}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.amount),
    },
    {
      // ⚠️ `LocalDate` — XOM ko'rsatiladi. Zona konvertatsiyasi kunni
      // siljitardi (`hiredAt` bilan bir xil qoida).
      title: t('hr.salary.validFrom'),
      dataIndex: 'validFrom',
      key: 'validFrom',
    },
    {
      title: t('hr.salary.reason'),
      key: 'reason',
      render: (_value, row) =>
        row.reason === null ? '—' : <ExpandableHint text={row.reason} />,
    },
    {
      title: t('hr.salary.changedBy'),
      key: 'changedBy',
      render: (_value, row) => actorLabel(row.changedBy, actors, t('common.dash')),
    },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 56,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_value: unknown, row: PayItem) => (
              <RowActions
                items={[
                  {
                    key: 'stop',
                    label: t('hr.salary.stopAction'),
                    onClick: () => {
                      setStopOn(tenantToday())
                      setStopTarget(row)
                    },
                    permission: 'hr.salary.write',
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ]

  const historyColumns: ColumnsType<PayItem> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, row) => row.componentName ? seededName('payComponents', row.componentCode, row.componentName, t) : (row.componentCode ?? '—'),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.amount),
    },
    { title: t('hr.salary.validFrom'), dataIndex: 'validFrom', key: 'validFrom' },
    {
      title: t('hr.salary.validTo'),
      key: 'validTo',
      render: (_value, row) =>
        row.validTo === null ? <Tag color="green">{t('hr.salary.open')}</Tag> : row.validTo,
    },
    {
      title: t('hr.salary.reason'),
      key: 'reason',
      render: (_value, row) =>
        row.reason === null ? '—' : <ExpandableHint text={row.reason} />,
    },
  ]

  const eventColumns: ColumnsType<PayEvent> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    { title: t('hr.payEvent.occurredOn'), dataIndex: 'occurredOn', key: 'occurredOn' },
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.componentName ? seededName('payComponents', row.componentCode, row.componentName, t) : (row.componentCode ?? '—')}</span>
          <Tag color={row.kind === 'EARNING' ? 'green' : 'volcano'}>
            {t(`payComponentKind.${row.kind}`)}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.amount),
    },
    {
      title: t('hr.payEvent.settled'),
      key: 'settled',
      render: (_value, row) =>
        row.settled ? (
          <Tooltip title={t('hr.payEvent.settledHint')}>
            <Tag color="blue">{t('hr.payEvent.settledYes')}</Tag>
          </Tooltip>
        ) : (
          <Tag>{t('hr.payEvent.settledNo')}</Tag>
        ),
    },
    {
      title: t('hr.payEvent.noteField'),
      key: 'note',
      render: (_value, row) => (row.note === null ? '—' : <ExpandableHint text={row.note} />),
    },
    {
      title: t('common.createdAt'),
      key: 'createdAt',
      render: (_value, row) =>
        row.createdAt === null ? (
          '—'
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>
            <span>{formatDateTime(row.createdAt)}</span>
          </Tooltip>
        ),
    },
  ]

  return (
    <Card
      title={t('hr.salary.title')}
      extra={
        canWrite ? (
          <Space wrap>
            <Button onClick={() => setSetOpen(true)}>{t('hr.salary.setAction')}</Button>
            <Button onClick={() => setEventOpen(true)}>{t('hr.payEvent.addAction')}</Button>
          </Space>
        ) : null
      }
    >
      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={runner.result}
            closable
            onClose={runner.clear}
          />
        </div>
      ) : null}

      {current.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(current.error, t)}
        />
      ) : null}

      <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('hr.salary.netEstimate')}>
          <Space direction="vertical" size={2}>
            <Typography.Text strong>
              {net === null ? '—' : formatMoneyValue(net)}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('hr.salary.netEstimateHint')}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.salary.openCount')}>{items.length}</Descriptions.Item>
      </Descriptions>

      <Table<PayItem>
        rowKey={(row) => row.id}
        columns={itemColumns}
        dataSource={[...items]}
        loading={current.isPending}
        pagination={false}
        size="small"
        scroll={{ x: 720 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('hr.salary.empty')}
            />
          ),
        }}
      />

      <Collapse
        ghost
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'history',
            label: t('hr.salary.historyTitle'),
            children: (
              <>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  {t('hr.salary.historyHint')}
                </Typography.Paragraph>
                <Table<PayItem>
                  rowKey={(row) => row.id}
                  columns={historyColumns}
                  dataSource={[...(history.data ?? [])]}
                  loading={history.isPending}
                  locale={history.isError ? { emptyText: translateError(history.error, t) } : undefined}
                  pagination={false}
                  size="small"
                  scroll={{ x: 640 }}
                />
              </>
            ),
          },
          {
            key: 'events',
            label: t('hr.payEvent.title'),
            children: (
              <>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  {t('hr.payEvent.hint')}
                </Typography.Paragraph>
                <Table<PayEvent>
                  rowKey={(row) => row.id}
                  columns={eventColumns}
                  dataSource={[...(events.data ?? [])]}
                  loading={events.isPending}
                  locale={events.isError ? { emptyText: translateError(events.error, t) } : undefined}
                  pagination={false}
                  size="small"
                  scroll={{ x: 760 }}
                />
              </>
            ),
          },
        ]}
      />

      <SalaryItemModal
        open={setOpen}
        personId={personId}
        components={recurringComponents}
        onClose={() => setSetOpen(false)}
      />

      <PayEventModal
        open={eventOpen}
        personId={personId}
        components={oneOffComponents}
        onClose={() => setEventOpen(false)}
      />

      {/*
        ⚠️ To'xtatish oynasi `useOperationRunner` bilan ishlaydi va
        natijani KARTOCHKADA ko'rsatadi (`guards.mjs` 1-bo'limi bunday
        oynalarni ataylab hisobga olmaydi). Sabab: javob
        `OperationResult` va u «o'zgarish yo'q: ochiq davr yo'q edi»
        degan holatni ham qaytaradi — u xato EMAS, lekin operator uni
        KO'RISHI kerak.
      */}
      <Modal
        open={stopTarget !== null}
        title={t('hr.salary.stopTitle')}
        onCancel={() => setStopTarget(null)}
        onOk={() => {
          const target = stopTarget
          if (!target) return
          void runner
            .run(() => stopSalaryItem(personId, target.componentId, stopOn.format(DATE_FORMAT)))
            .then((result) => {
              if (result.status !== 'FAILED') setStopTarget(null)
            })
        }}
        confirmLoading={runner.running}
        okText={t('hr.salary.stopAction')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.cancel')}
        destroyOnHidden
        width={520}
      >
        <div style={{ marginBottom: 16 }}>
          <ExpandableHint text={t('hr.salary.stopNote')} fontSize={13} />
        </div>
        <Typography.Text>{t('hr.salary.stopOn')}</Typography.Text>
        <DatePicker
          value={stopOn}
          onChange={(value) => {
            if (value) setStopOn(value)
          }}
          format={DATE_FORMAT}
          allowClear={false}
          disabledDate={(day) => stopTarget !== null && day.format(DATE_FORMAT) < stopTarget.validFrom}
          style={{ width: '100%', marginTop: 8 }}
        />
      </Modal>
    </Card>
  )
}
